import React, { useCallback } from 'react';
import {
  Box,
  Card,
  CardBody,
  Dropdown,
  Heading,
} from '@hawkins/components';
import { useMap } from 'react-use';
import { useQueryParam } from 'use-query-params';

import {
  calculatorOptions,
  CalculatorKey,
  CalculatorMap,
  CalculatorInputMap,
  CalculatorOutputMap,
  CalculatorInputChangeEventHandler,
  DropdownCalculatorOption,
} from 'src/domains/calculators';
import { Grid } from 'src/domains/shared/components/Grid';
import { createEncodedObjectParam } from 'src/domains/shared/hooks/useQueryParams/EncodedObjectParam';

import { CalculatorRenderer } from './CalculatorRenderer';
import { Dimensions } from '@hawkins/variables';

export const CalculatorSelection = () => {
  const [selectedCalculators, setSelectedCalculators] = useQueryParam(
    'calculators',
    createEncodedObjectParam<
      DropdownCalculatorOption[],
      DropdownCalculatorOption[]
    >()
  );

  const calculators = (selectedCalculators ?? []).map(
    (option) => CalculatorMap[option.value]
  );

  const [inputMap, inputMapActions] = useMap<CalculatorInputMap>();
  const [outputMap, outputMapActions] = useMap<CalculatorOutputMap>();

  const onInputChange =
    useCallback<CalculatorInputChangeEventHandler>(
      (key, input, output) => {
        inputMapActions.set(key, input);
        outputMapActions.set(key, output);
      },
      /**
       * TODO: for some reason, setting
       * [inputMapActions, outputMapActions]
       * is triggering an infinite loop
       * when selecting a calculator.
       *
       * this is probably due to the
       * output map being updated, but still
       * ideally the outputMapActions should
       * not change
       *
       * so just ignoring this for now,
       * but gotta figure out a clean fix
       */
      // eslint-disable-next-line
      []
    );

  const removeOutputForKey = useCallback(
    (key: keyof CalculatorOutputMap) => {
      outputMapActions.remove(key);
    },
    [outputMapActions]
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Grid gap={Dimensions.Space16} columns={'minmax(0, 1200px)'}>
        <Card>
          <CardBody>
            <Grid gap={Dimensions.Space8}>
              <Heading>Select Calculators</Heading>
              <Dropdown
                multi
                clearable
                searchable
                placeholder="Select calculators"
                options={calculatorOptions}
                value={selectedCalculators ?? []}
                onChange={(
                  newValues: DropdownCalculatorOption[] | null
                ) => {
                  if (newValues === null) {
                    return;
                  }

                  setSelectedCalculators(newValues);

                  // remove calculators that were previously
                  // selected from the input and output map
                  Object.keys(inputMap)
                    .filter((key) => {
                      return !newValues.find(
                        ({ value }) => value === key
                      );
                    })
                    .forEach((missingKey) => {
                      const key = missingKey as CalculatorKey;

                      inputMapActions.remove(key);
                      outputMapActions.remove(key);
                    });
                }}
              />
            </Grid>
          </CardBody>
        </Card>

        <CalculatorRenderer
          inputMap={inputMap}
          outputMap={outputMap}
          onInputReset={removeOutputForKey}
          onInputChange={onInputChange}
          selectedCalculators={calculators}
        />
      </Grid>
    </Box>
  );
};
